import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useMotionTemplate,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";

/**
 * Three-dimensional motion primitives.
 *
 * Built on CSS 3D transforms rather than WebGL, and that is a deliberate
 * trade. A three.js scene is roughly six hundred kilobytes before it draws a
 * single triangle, on the one page whose load time decides whether anybody
 * sees the product at all -- and it asks a phone for a GPU context to render
 * what is, in the end, some cards at different depths. `perspective` and
 * `translateZ` are composited on the GPU too, cost nothing to ship, and
 * degrade to flat rather than to blank on hardware that cannot manage them.
 *
 * Two rules carry over from the flat primitives next door, because they matter
 * more here, not less.
 *
 * Nothing animates in from invisible. Depth is applied to content that has
 * already painted, so the page is whole in its first frame -- which is what a
 * link preview, a search crawler and a reader on a slow connection all get.
 *
 * prefers-reduced-motion removes the movement and keeps everything else.
 * Parallax and tilt are exactly the effects that make people ill, so under
 * that setting these become plain, static, correctly laid out elements. Not
 * degraded -- just still.
 */

const SPRING = { stiffness: 150, damping: 20, mass: 0.6 };

/**
 * A surface that turns to follow the cursor.
 *
 * The rotation is deliberately small. Past about twelve degrees the text on a
 * card starts to distort and read as a gimmick rather than as depth, so the
 * default stays well inside that.
 */
export function TiltCard({
  children,
  className = "",
  strength = 9,
  glare = true,
  ...rest
}) {
  const reduce = useReducedMotion();
  const ref = useRef(null);

  // -0.5 .. 0.5, the pointer's position within the element.
  const px = useMotionValue(0);
  const py = useMotionValue(0);

  const rotateX = useSpring(useTransform(py, [-0.5, 0.5], [strength, -strength]), SPRING);
  const rotateY = useSpring(useTransform(px, [-0.5, 0.5], [-strength, strength]), SPRING);

  // The highlight tracks the pointer as though a light source sat above it.
  const glareX = useTransform(px, [-0.5, 0.5], ["0%", "100%"]);
  const glareY = useTransform(py, [-0.5, 0.5], ["0%", "100%"]);
  const glareBg = useMotionTemplate`radial-gradient(circle at ${glareX} ${glareY}, rgba(255,255,255,0.35), transparent 60%)`;

  function onMove(event) {
    const node = ref.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    px.set((event.clientX - rect.left) / rect.width - 0.5);
    py.set((event.clientY - rect.top) / rect.height - 0.5);
  }

  function onLeave() {
    px.set(0);
    py.set(0);
  }

  if (reduce) return <div className={className} {...rest}>{children}</div>;

  return (
    <motion.div
      ref={ref}
      className={`group relative ${className}`}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
      {...rest}
    >
      {children}
      {glare ? (
        <motion.div
          aria-hidden="true"
          // group-hover, not hover: this layer is pointer-events-none so it can
          // never be hovered itself -- keyed to its own :hover the highlight
          // would simply never appear.
          className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          style={{ background: glareBg, mixBlendMode: "soft-light" }}
        />
      ) : null}
    </motion.div>
  );
}

/**
 * The container a tilt or a depth stack needs above it.
 *
 * Perspective has to live on the ancestor, not on the thing being rotated --
 * put it on the element itself and every child shares one vanishing point,
 * which reads as a shear rather than as depth. Larger values are a longer
 * lens: 1200-1600px is enough to see, without the wide-angle distortion that
 * makes a card look like it is falling over.
 */
export function Scene({ children, className = "", depth = 1400 }) {
  return (
    <div className={className} style={{ perspective: `${depth}px` }}>
      {children}
    </div>
  );
}

/**
 * A layer sitting at a fixed distance from the page, drifting gently.
 *
 * translateZ inside a preserve-3d parent is what actually separates the
 * layers: nearer things swing further as the scene turns, which is the cue the
 * eye reads as depth. The drift keeps a static hero from looking like a
 * screenshot without ever moving far enough to distract.
 */
export function Layer({
  children,
  z = 0,
  className = "",
  float = 0,
  delay = 0,
  ...rest
}) {
  const reduce = useReducedMotion();

  if (reduce || !float) {
    return (
      <div className={className} style={{ transform: `translateZ(${z}px)` }} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      className={className}
      style={{ translateZ: z, transformStyle: "preserve-3d" }}
      animate={{ y: [0, -float, 0] }}
      transition={{
        duration: 5 + delay,
        repeat: Infinity,
        ease: "easeInOut",
        delay,
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/**
 * A whole scene that turns as the cursor crosses it.
 *
 * Tracked on the section rather than on each card, so the group holds together
 * as one object with a single vanishing point instead of a dozen cards each
 * pivoting on their own.
 */
export function PointerScene({ children, className = "", strength = 6, depth = 1600 }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const px = useMotionValue(0);
  const py = useMotionValue(0);

  const rotateX = useSpring(useTransform(py, [-0.5, 0.5], [strength, -strength]), {
    stiffness: 60,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(px, [-0.5, 0.5], [-strength, strength]), {
    stiffness: 60,
    damping: 20,
  });

  function onMove(event) {
    const node = ref.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    px.set((event.clientX - rect.left) / rect.width - 0.5);
    py.set((event.clientY - rect.top) / rect.height - 0.5);
  }

  if (reduce) return <div className={className}>{children}</div>;

  return (
    <div
      ref={ref}
      className={className}
      onPointerMove={onMove}
      onPointerLeave={() => { px.set(0); py.set(0); }}
      style={{ perspective: `${depth}px` }}
    >
      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}>
        {children}
      </motion.div>
    </div>
  );
}

/**
 * Moves at a different rate to the page as it scrolls.
 *
 * `speed` is a fraction of the travel: positive lags behind the scroll,
 * negative runs ahead of it. Kept small, because parallax that outpaces the
 * page is the effect people describe as making a site feel broken.
 */
export function Parallax({ children, speed = 0.15, className = "" }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [`${speed * 100}%`, `${-speed * 100}%`]);

  if (reduce) return <div className={className}>{children}</div>;

  return (
    <div ref={ref} className={className}>
      <motion.div style={{ y }}>{children}</motion.div>
    </div>
  );
}

/**
 * Rises out of the page and settles as it scrolls into view.
 *
 * Starts at a legible opacity rather than zero: if the scroll listener never
 * runs -- a crawler, a reader who lands mid-page, a browser that throttles
 * background frames -- the content is still there to read.
 */
export function ScrollLift({ children, className = "", distance = 60 }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "center center"],
  });

  const rotateX = useTransform(scrollYProgress, [0, 1], [distance / 6, 0]);
  const y = useTransform(scrollYProgress, [0, 1], [distance, 0]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [0.35, 1]);

  if (reduce) return <div className={className}>{children}</div>;

  return (
    <div ref={ref} className={className} style={{ perspective: "1200px" }}>
      <motion.div style={{ rotateX, y, opacity, transformOrigin: "50% 100%" }}>
        {children}
      </motion.div>
    </div>
  );
}

/**
 * A control that leans toward the cursor as it approaches.
 *
 * The element moves; the hit area does not. A button that dodges the pointer
 * is a button people miss, so the travel is a few pixels and the underlying
 * box stays where it was.
 */
export function Magnetic({ children, className = "", pull = 6 }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const x = useSpring(useMotionValue(0), SPRING);
  const y = useSpring(useMotionValue(0), SPRING);

  function onMove(event) {
    const node = ref.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    x.set(((event.clientX - rect.left) / rect.width - 0.5) * pull * 2);
    y.set(((event.clientY - rect.top) / rect.height - 0.5) * pull * 2);
  }

  if (reduce) return <span className={className}>{children}</span>;

  return (
    <motion.span
      ref={ref}
      className={`inline-block ${className}`}
      onPointerMove={onMove}
      onPointerLeave={() => { x.set(0); y.set(0); }}
      style={{ x, y }}
    >
      {children}
    </motion.span>
  );
}

/**
 * A row that scrolls sideways for ever.
 *
 * The children are rendered twice and the track is translated by exactly half
 * its width, so the seam lands on an identical frame and the loop is
 * invisible. Marked aria-hidden and duplicated, so a screen reader is not read
 * the same list of names twice.
 */
export function Marquee({ children, className = "", duration = 32, reverse = false }) {
  const reduce = useReducedMotion();

  if (reduce) {
    return <div className={`flex flex-wrap justify-center gap-3 ${className}`}>{children}</div>;
  }

  return (
    <div className={`hs-marquee-mask overflow-hidden ${className}`}>
      <motion.div
        className="flex w-max gap-3"
        animate={{ x: reverse ? ["-50%", "0%"] : ["0%", "-50%"] }}
        transition={{ duration, repeat: Infinity, ease: "linear" }}
      >
        <div className="flex shrink-0 gap-3">{children}</div>
        <div className="flex shrink-0 gap-3" aria-hidden="true">{children}</div>
      </motion.div>
    </div>
  );
}
